/**
 * Cross-tab pre-logout handshake — BUG-146069.
 *
 * `preLogoutTasks` releases what THIS tab holds when THIS tab signs out. It
 * cannot help the tab next door: sign-out in tab A broadcasts `LOGOUT` on the
 * auth channel, and tab B's handler (`authSync`) only calls `clearAuthState()`
 * and navigates. Tab B never sends `/close`, so the steering document it had
 * open stays exclusively locked — the reported bug, from a second tab.
 *
 * Tab B cannot fix that after the fact either. The auth cookies live in ONE
 * shared jar, so by the time tab A's `POST /api/auth/logout` has returned, tab
 * B's credentials are already gone and its release would 401 — the same trap
 * that rules out doing this from an `auth:logout` listener in the first place.
 *
 * So tab A asks first, and waits:
 *
 *   1. tab A broadcasts `request` BEFORE its logout POST, while the whole
 *      browser is still authenticated;
 *   2. every tab that actually holds something answers `ack` and starts its own
 *      `runPreLogoutTasks()`, then answers `done`;
 *   3. tab A resolves once every tab that acked is done — or when the budget
 *      runs out, whichever comes first.
 *
 * A tab holding nothing stays silent, so the common single-tab sign-out pays
 * only {@link PEER_DISCOVERY_TIMEOUT_MS} of silence.
 *
 * Constraints worth preserving:
 *
 * - This runs on its OWN BroadcastChannel, not the `helios-auth-channel` one.
 *   That keeps the handshake out of the LOGIN/LOGOUT/TOKEN_REFRESH protocol
 *   (whose `LOGOUT` is derived from a store subscription, and would fire at the
 *   wrong moment for this) and keeps this module free of any import of
 *   `authSync`/`authStore` — the initiator side is imported BY `authStore`.
 * - Nothing here may throw or hang: sign-out proceeds regardless. The backend
 *   logout subscriber is the backstop for anything that does not answer in time.
 */

import { hasPreLogoutTasks, runPreLogoutTasks, type PreLogoutBatchResult } from './preLogoutTasks';

const CHANNEL_NAME = 'helios-pre-logout';

/**
 * How long the signing-out tab waits for any peer to declare that it holds
 * something. BroadcastChannel delivery is same-origin and effectively immediate;
 * this only has to cover a peer's event-loop turn.
 */
export const PEER_DISCOVERY_TIMEOUT_MS = 250;

/**
 * Ceiling on the whole handshake once a peer HAS answered. Matches the local
 * task ceiling, so a peer's stalled release delays sign-out by the same bounded
 * amount as this tab's own would.
 */
export const PEER_RELEASE_TIMEOUT_MS = 3000;

type PreLogoutMessage =
  | { kind: 'request'; requestId: string }
  | { kind: 'ack'; requestId: string; peerId: string }
  /**
   * `result` is BUG-146069-002. `done` used to be posted unconditionally, because the batch it
   * followed never rejects — so the signing-out tab received a positive acknowledgement for a
   * release that may have failed entirely, and only the answering tab ever knew. It carries the
   * batch outcome now. Optional, so a peer running older code still completes the handshake.
   */
  | { kind: 'done'; requestId: string; peerId: string; result?: PreLogoutBatchResult };

/** Identifies this tab in the handshake. Stable for the life of the document. */
const peerId = Math.random().toString(36).slice(2);

let channel: BroadcastChannel | null = null;
let responderAttached = false;

/**
 * Lazily opens the shared channel. Returns null where BroadcastChannel is
 * unavailable (an older browser, or a test environment that does not provide
 * it), which turns every function here into a no-op rather than an error.
 */
function getChannel(): BroadcastChannel | null {
  if (channel) return channel;
  if (typeof BroadcastChannel === 'undefined') return null;

  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    channel = null;
  }

  return channel;
}

function post(message: PreLogoutMessage): void {
  try {
    getChannel()?.postMessage(message);
  } catch {
    // A closed or unavailable channel must never break sign-out.
  }
}

/**
 * Makes this tab answer another tab's sign-out. Called from
 * `initializeAuthSync()`, which every tab runs on mount, and idempotent so
 * repeat calls (or a remote re-initialising) attach a single listener.
 *
 * A tab with nothing registered stays silent — it neither acks nor delays the
 * signing-out tab.
 */
export function initializePreLogoutPeers(): void {
  if (responderAttached) return;

  const bus = getChannel();
  if (!bus) return;

  responderAttached = true;

  bus.addEventListener('message', (event: MessageEvent<PreLogoutMessage>) => {
    const message = event.data;
    if (message?.kind !== 'request') return;
    // Silence is the answer when this tab holds no server-side resource.
    if (!hasPreLogoutTasks()) return;

    const { requestId } = message;
    post({ kind: 'ack', requestId, peerId });

    // Non-throwing and self-bounded by contract (see runPreLogoutTasks), so
    // `done` is always sent and the peer is never left waiting out its budget.
    //
    // BUG-146069-002 — `done` now carries HOW it went. It is still sent unconditionally, because
    // the signing-out tab must never wait out its budget on us; what changed is that "I released
    // everything" and "every release I attempted failed" are no longer the same message.
    void runPreLogoutTasks().then((result) => {
      post({ kind: 'done', requestId, peerId, result });
    });
  });
}

/**
 * Asks every other tab to hand back what it holds, and resolves when they have
 * (or when the budget expires). Awaited by `useAuthStore.logout()` alongside
 * this tab's own tasks, BEFORE the logout POST invalidates the shared cookies.
 *
 * Never rejects.
 */
export function requestPeerPreLogoutRelease(
  discoveryTimeoutMs: number = PEER_DISCOVERY_TIMEOUT_MS,
  releaseTimeoutMs: number = PEER_RELEASE_TIMEOUT_MS
): Promise<void> {
  const maybeBus = getChannel();
  if (!maybeBus) return Promise.resolve();

  // Typed non-nullable so the hoisted helpers below need no narrowing.
  const bus: BroadcastChannel = maybeBus;

  const requestId = `${peerId}:${Date.now()}:${Math.random().toString(36).slice(2)}`;

  return new Promise<void>((resolve) => {
    const pending = new Set<string>();
    let discoveryTimer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;

    // Function declarations, not arrows: they hoist, so `finish` can clear the
    // `const` timer declared below it and the timer can still be armed with it.
    function finish() {
      if (settled) return;
      settled = true;
      if (discoveryTimer !== undefined) clearTimeout(discoveryTimer);
      clearTimeout(releaseTimer);
      bus.removeEventListener('message', onMessage);
      resolve();
    }

    function onMessage(event: MessageEvent<PreLogoutMessage>) {
      const message = event.data;
      if (!message || message.requestId !== requestId) return;

      if (message.kind === 'ack') {
        // At least one peer holds something: stop counting down the discovery
        // window and give it the full release budget instead.
        if (discoveryTimer !== undefined) {
          clearTimeout(discoveryTimer);
          discoveryTimer = undefined;
        }
        pending.add(message.peerId);
        return;
      }

      if (message.kind === 'done') {
        pending.delete(message.peerId);
        // BUG-146069-002 — a peer that answered `done` having released nothing is the one case
        // where another tab's document stays locked and THIS tab is the only place it could be
        // noticed: the peer is navigating away to /login as it speaks.
        const result = message.result;
        if (result && (result.failed > 0 || result.timedOut)) {
          console.warn(
            `[BUG-146069] peer ${message.peerId} reported a release that did not complete ` +
              `(${result.failed} failed, ${result.settled}/${result.total} settled` +
              `${result.timedOut ? ', ceiling hit' : ''}) — a document it held may still be locked.`
          );
        }
        // Only meaningful once a peer has acked; an unmatched `done` cannot
        // empty a set that was never filled.
        if (pending.size === 0) finish();
      }
    }

    bus.addEventListener('message', onMessage);

    // Hard ceiling, armed up front: a peer that acks and then dies must not
    // strand the sign-out.
    //
    // BUG-146069-002 — and it says so. `finish` resolves identically whether the last `done`
    // arrived or this timer fired, so a peer that acked and then crashed was indistinguishable
    // from one that finished cleanly. The peers still owing an answer are named here because
    // after `resolve()` nothing knows them: `pending` goes out of scope with the handshake.
    const releaseTimer = setTimeout(() => {
      if (pending.size > 0) {
        console.warn(
          `[BUG-146069] the cross-tab release handshake hit its ${releaseTimeoutMs}ms ceiling with ` +
            `${pending.size} peer(s) still owing an answer (${Array.from(pending).join(', ')}) — ` +
            'sign-out proceeded; documents those tabs held may still be locked.'
        );
      }
      finish();
    }, releaseTimeoutMs);
    // Fast path for the common case — nobody else is holding anything. Silent by design: no peer
    // answering means no peer holds anything, which is the ordinary single-tab sign-out.
    discoveryTimer = setTimeout(() => {
      if (pending.size === 0) finish();
    }, discoveryTimeoutMs);

    post({ kind: 'request', requestId });
  });
}

/** Test seam: drops the responder and the channel so suites cannot leak state. */
export function resetPreLogoutPeers(): void {
  try {
    channel?.close();
  } catch {
    // Already closed — nothing to do.
  }

  channel = null;
  responderAttached = false;
}
